import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFieldArray, useForm } from 'react-hook-form';
import { api } from '../api/client';
import { useState } from 'react';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type SalesOrder = {
  id: number;
  customer_id: number;
  customer_name: string;
  status: string;
  order_date: string;
  reference?: string;
  item_count: number;
};

type SalesOrderForm = {
  customer_id: number;
  order_date?: string;
  reference?: string;
  notes?: string;
  items: Array<{
    product_id: number;
    quantity: number;
    unit_price: number;
  }>;
};

type CustomerForm = {
  name: string;
  email?: string;
};

const SalesOrdersPage = () => {
  const queryClient = useQueryClient();
  const ordersQuery = useQuery({
    queryKey: ['sales-orders'],
    queryFn: () => api.get<SalesOrder[]>('/api/sales-orders')
  });

  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/api/customers')
  });

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<Array<{ id: number; name: string; price_per_unit: number }>>('/api/products')
  });

  const form = useForm<SalesOrderForm>({
    defaultValues: {
      items: [{ product_id: 0, quantity: 1, unit_price: 15 }]
    }
  });
  const items = useFieldArray({ name: 'items', control: form.control });

  const createOrder = useMutation({
    mutationFn: (values: SalesOrderForm) =>
      api.post('/api/sales-orders', {
        ...values,
        customer_id: Number(values.customer_id),
        items: values.items
          .filter((item) => item.product_id)
          .map((item) => ({
            product_id: Number(item.product_id),
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price)
          }))
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      form.reset({ customer_id: 0, items: [{ product_id: 0, quantity: 1, unit_price: 15 }] });
    }
  });

  const createCustomer = useMutation({
    mutationFn: (values: CustomerForm) => api.post('/api/customers', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    }
  });

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<keyof SalesOrder>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'history' | 'order' | 'customer'>('history');
  const orders = useMemo(() => {
    const rows = (ordersQuery.data ?? []).filter((o) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      const hay = [String(o.id), o.customer_name, o.status, o.order_date, o.reference ?? '', String(o.item_count)]
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const va = a[sortKey] as unknown;
      const vb = b[sortKey] as unknown;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va ?? '').localeCompare(String(vb ?? '')) * dir;
    });
  }, [ordersQuery.data, filter, sortKey, sortDir]);

  return (
    <div>
      <h2>Sales Orders</h2>
      <p style={{ color: '#64748b' }}>
        Capture wholesale shipments. When an order is shipped the worker decrements
        inventory and tracks fulfilment metrics.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'history', label: 'Recent Sales', badge: orders.length },
          { id: 'order', label: 'Create Sales Order' },
          { id: 'customer', label: 'Quick-Add Customer' }
        ]}
      />

      {activeTab === 'customer' ? (
      <div className="card" style={{ marginTop: 16 }}>
        <h3>Quick-add customer</h3>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget as HTMLFormElement);
            const name = String(data.get('name') ?? '');
            const email = data.get('email');
            if (!name) return;
            createCustomer.mutate({ name, email: email ? String(email) : undefined });
            (event.currentTarget as HTMLFormElement).reset();
          }}
        >
          <div className="form-group">
            <label>Name</label>
            <input name="name" placeholder="Cafe Partner" required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input name="email" type="email" placeholder="hello@partner.com" />
          </div>
          <button className="btn secondary" type="submit" disabled={createCustomer.isPending}>
            {createCustomer.isPending ? 'Saving…' : 'Add customer'}
          </button>
        </form>
      </div>
      ) : null}

      {activeTab === 'order' ? (
      <div className="card" style={{ marginTop: 16 }}>
        <h3>Create sales order</h3>
        <form className="form-grid" onSubmit={form.handleSubmit((values) => createOrder.mutate(values))}>
          <div className="form-group">
            <label>Customer</label>
            <select {...form.register('customer_id', { required: true, valueAsNumber: true })}>
              <option value="">Select customer</option>
              {customersQuery.data?.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Order date</label>
            <input type="date" {...form.register('order_date')} />
          </div>
          <div className="form-group">
            <label>Reference</label>
            <input {...form.register('reference')} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Notes</label>
            <textarea rows={2} {...form.register('notes')} />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <h4>Line items</h4>
            {items.fields.map((field, index) => (
              <div
                key={field.id}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, marginBottom: 8 }}
              >
                <select {...form.register(`items.${index}.product_id` as const, { valueAsNumber: true })}>
                  <option value="">Select product</option>
                  {productsQuery.data?.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.1"
                  {...form.register(`items.${index}.quantity` as const, { valueAsNumber: true })}
                />
                <input
                  type="number"
                  step="0.01"
                  {...form.register(`items.${index}.unit_price` as const, { valueAsNumber: true })}
                />
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => items.remove(index)}
                  disabled={items.fields.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn secondary"
              onClick={() => items.append({ product_id: 0, quantity: 1, unit_price: 15 })}
              style={{ marginTop: 8 }}
            >
              Add line
            </button>
          </div>

          <button className="btn primary" type="submit" disabled={createOrder.isPending}>
            {createOrder.isPending ? 'Creating…' : 'Create sales order'}
          </button>
        </form>
      </div>
      ) : null}

      {activeTab === 'history' ? (
      <section style={{ marginTop: 8 }}>
        <h3>Recent sales</h3>
        <TableToolbar value={filter} onChange={setFilter} placeholder="Filter sales orders…" />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {([
                  { key: 'id', label: 'ID' },
                  { key: 'customer_name', label: 'Customer' } as any,
                  { key: 'status', label: 'Status' },
                  { key: 'order_date', label: 'Date' } as any,
                  { key: 'reference', label: 'Reference' } as any,
                  { key: 'item_count', label: 'Items' } as any
                ] as Array<{ key: keyof SalesOrder | any; label: string }> ).map((col) => (
                  <th
                    key={String(col.key)}
                    onClick={() => {
                      if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      setSortKey(col.key as keyof SalesOrder);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {col.label}
                    {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.customer_name}</td>
                  <td>
                    <span className={`status-chip ${order.status}`}>{order.status}</span>
                  </td>
                  <td>{new Date(order.order_date).toLocaleDateString()}</td>
                  <td>{order.reference ?? '—'}</td>
                  <td>{order.item_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}
    </div>
  );
};

export default SalesOrdersPage;
